import React, { useRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';
import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox/typed';
import { observer } from 'mobx-react';
import config from '../../../config';
import { PANEL_BORDER_COLOR } from '../../../constants';
import { getMapCursor } from './utils/getMapCursor';
import useCursorProps from '../../../hooks/useCursorProps';
import ReactMapGL, { useControl, ViewStateChangeEvent, ViewState, MapRef } from 'react-map-gl';
import MapActionBar from '../../orders/OrderLocationsMap/MapActionBar';
import { useResolvedV5Theme } from '../../../hooks/useV5Theme';
import { PickingInfo } from '@deck.gl/core/typed';

const MAP_STYLE_URLS = {
  light: 'mapbox://styles/routific/cmrttq0v4006i01rj549cfc83',
  dark: 'mapbox://styles/routific/cmrtts9va007i01sra6jr51yx',
} as const;

interface Props extends Partial<any> {
  viewState?: ViewState;
  onViewStateChange?: (evt: any) => void;
  isDraggable: boolean;
  children?: React.ReactNode;
  mapChildren?: React.ReactNode;
  className?: string;
  ref?: any;
  bordered?: boolean;
  roundedCorners?: boolean;
  hoveredMarker?: boolean;
  layers?: any[];
  onMove?: (viewState: ViewState) => void;
  getCursor?: (interactiveState?: any) => string;
  onClear?: () => void;
  onClick?: ((info: PickingInfo) => void) | null;
  onHover?: ((info: PickingInfo) => void) | null;
}

const DeckGLOverlay = React.forwardRef<MapboxOverlay, MapboxOverlayProps & { interleaved?: boolean; }>((props, ref) => {
  const overlayRef = useControl<MapboxOverlay>(() => new MapboxOverlay(props));

  useImperativeHandle(ref, () => overlayRef, [overlayRef]);

  useEffect(() => {
    if (overlayRef) {
      overlayRef.setProps(props);
    }
  }, [props, overlayRef]);

  return null;
});

const MAPBOX_TOKEN = config.thirdParty.mapboxToken;

const MapDiv = styled.div<{ bordered?: boolean, roundedCorners?: boolean }>`
  height: 100%;
  width: 100%;
  min-height: 140px;
  z-index: 1; // Fixes funky clipping of the map within the order modal
  position: relative;
  ${({ roundedCorners }) => roundedCorners && `
    border-radius: 16px;
  `}
  ${({ bordered }) => bordered && `
    border: 1px solid ${PANEL_BORDER_COLOR};
    overflow: hidden;
  `}
`;

export interface MapsRef {
  reactMapGLRef: MapRef | null;
  deckGLRef: MapboxOverlay | null;
}

const MapBase = React.forwardRef<MapsRef, Props>((props, ref) => {
  const cursorProps = useCursorProps();
  const { colorMode } = useResolvedV5Theme();
  const reactMapGLRef = useRef<MapRef>(null);
  const deckGLRef = useRef<MapboxOverlay>(null);

  useImperativeHandle(ref, () => ({
    reactMapGLRef: reactMapGLRef.current,
    deckGLRef: deckGLRef.current,
  }), [reactMapGLRef.current, deckGLRef.current]);

  const {
    isDraggable,
    mapChildren,
    children,
    className,
    bordered = true,
    roundedCorners = true,
    hoveredMarker,
    hoveredSuggestion,
    onMove,
    getCursor,
    isPinDropMode,
    onClear,
    onClick,
    onHover,
    doubleClickZoom,
    mode,
    ...restProps
  } = props;

  const getDefaultCursor = (interactiveState: any) => {
    const cursorVars = {
      ...cursorProps,
      hoveredMarker,
      hoveredSuggestion,
      isPanningMap: interactiveState.isDragging,
    };
    return getMapCursor(cursorVars);
  };

  return (
    <MapDiv className={className} bordered={bordered} roundedCorners={roundedCorners} data-testid="map">
      <ReactMapGL
        ref={reactMapGLRef}
        touchZoomRotate={isDraggable}
        scrollZoom={isDraggable}
        mapStyle={MAP_STYLE_URLS[colorMode]}
        style={{ width: '100%', height: '100%' }}
        mapboxAccessToken={MAPBOX_TOKEN}
        antialias={true}
        doubleClickZoom={doubleClickZoom}
        onMove={(evt: ViewStateChangeEvent) => {
          const { viewState } = evt;
          if (onMove) {
            onMove(viewState);
          }
        }}
        dragPan={isDraggable}
        {...restProps.viewState}
      >
        <DeckGLOverlay
          ref={deckGLRef}
          layers={restProps.layers || []}
          interleaved={true}
          getCursor={getCursor || getDefaultCursor}
          onClick={onClick}
          onHover={onHover}
          pickingRadius={4} // radius for hover, click interactions
        />
        {mapChildren}
      </ReactMapGL>
      {children}
      <MapActionBar onClear={onClear} isVisible={isPinDropMode} />
    </MapDiv>
  );
});

export default observer(MapBase);
